/**
 * PromocionesInternet.tsx — Planes de internet (ruta /menu/promociones-internet).
 *
 * Arriba, un carrusel de 3 banners que se pasa solo cada 5 segundos hasta que
 * el usuario lo toca. Debajo, los planes para elegir uno, y un botón fijo que
 * abre el WhatsApp de ventas con el plan elegido ya escrito en el mensaje.
 * El botón "Compartir" de la cabecera arma una imagen con todos los planes para
 * mandarla por WhatsApp o enseñársela a un cliente (como el comprobante de pago).
 *
 * Los planes están FIJOS en la app (src/data/planesInternet.ts), copiados de
 * https://www.sisnetel.com/#planes.
 *
 * Visible para todo el mundo: clientes y administradores.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaCheck, FaShareAlt, FaWhatsapp } from 'react-icons/fa';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import CompartirPlanesModal from '../components/CompartirPlanesModal';
import {
  PLANES_INTERNET as PLANES, MEGAS_MAXIMO, PRECIO_MINIMO, INCLUYE_TODOS as INCLUYE, WHATSAPP_VENTAS,
} from '../data/planesInternet';
import './PromocionesInternet.css';

const BackIcon      = FaArrowLeft  as unknown as React.FC;
const FlechaIcon    = FaArrowRight as unknown as React.FC;
const CheckIcon     = FaCheck      as unknown as React.FC;
const CompartirIcon = FaShareAlt   as unknown as React.FC;
const WhatsAppIcon  = FaWhatsapp   as unknown as React.FC;

// Cada cuánto pasa solo el carrusel
const INTERVALO_CARRUSEL_MS = 5000;

type Banner = {
  clave: string;
  variante?: 'rojo' | 'noche';
  kicker: string;
  previo?: string;     // texto pequeño antes de la cifra, ej. "desde"
  numero: string;
  unidad: string;
  texto: string;
  accion: string;
  planId?: string;     // si lo tiene, tocar el banner elige ese plan
};

const BANNERS: Banner[] = [
  {
    clave: 'desde', kicker: 'Fibra óptica SisNetel', previo: 'desde', numero: `$${PRECIO_MINIMO}`, unidad: 'al mes',
    texto: 'El precio que ves es el que pagas: IVA incluido y sin costo de instalación.', accion: 'Ver planes',
  },
  {
    clave: '700', variante: 'rojo', kicker: 'El más pedido', numero: '700', unidad: 'Megas', planId: '700',
    texto: 'El equilibrio que elige la mayoría. Hasta 25 dispositivos a la vez.', accion: 'Lo quiero',
  },
  {
    clave: '1000', variante: 'noche', kicker: 'Toda la fibra', numero: '1', unidad: 'Giga', planId: '1000',
    texto: 'Sin límites artificiales. Dispositivos ilimitados y cámaras 24/7.', accion: 'Lo quiero',
  },
];

// Hilos de fibra del fondo de cada banner (coordenadas de un lienzo de 200x200)
const FIBRAS = [
  'M 0 150 C 70 125, 120 40, 200 0',
  'M 20 200 C 90 150, 110 70, 200 40',
  'M 70 200 C 120 165, 150 110, 200 95',
  'M 120 200 C 150 180, 170 160, 200 150',
];

/** Hilos de fibra con pulsos de luz que viajan por ellos (solo decoración). */
const FibraDecorativa: React.FC = () => (
  <svg className="pi-slide-fibra" viewBox="0 0 200 200" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
    {FIBRAS.map(d => <path key={d} d={d} className="pi-fibra-linea" pathLength={256} />)}
    {FIBRAS.slice(0, 3).map((d, i) => (
      <path key={`pulso-${d}`} d={d} className={`pi-fibra-pulso pi-fibra-pulso--${i + 1}`} pathLength={256} />
    ))}
  </svg>
);

const PromocionesInternet: React.FC = () => {
  const navigate = useNavigate();

  const clientDataJSON = localStorage.getItem('clientData');
  const clientData = clientDataJSON ? JSON.parse(clientDataJSON) : null;
  const esAdmin = Boolean(clientData?.token_cliente?.trim());

  // Arranca elegido el plan que la web marca como "El más pedido"
  const [planId, setPlanId] = useState('700');
  const plan = PLANES.find(p => p.id === planId) ?? null;

  const [mostrarCompartir, setMostrarCompartir] = useState(false);

  // ── Carrusel ────────────────────────────────────────────────────────────────
  const carruselRef = useRef<HTMLDivElement | null>(null);
  const listaRef = useRef<HTMLDivElement | null>(null);
  const [bannerActivo, setBannerActivo] = useState(0);
  // El intervalo lee estos valores sin reiniciarse en cada render
  const bannerActivoRef = useRef(0);
  const autoplayRef = useRef(true);

  const irABanner = (idx: number) => {
    const el = carruselRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
  };

  const alDesplazarCarrusel = () => {
    const el = carruselRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / (el.clientWidth || 1));
    if (idx !== bannerActivoRef.current) {
      bannerActivoRef.current = idx;
      setBannerActivo(idx);
    }
  };

  // En cuanto el usuario toca el carrusel, deja de pasar solo
  const detenerAutoplay = () => { autoplayRef.current = false; };

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      if (!autoplayRef.current) return;
      irABanner((bannerActivoRef.current + 1) % BANNERS.length);
    }, INTERVALO_CARRUSEL_MS);
    return () => window.clearInterval(id);
  }, []);

  const tocarBanner = (banner: Banner) => {
    detenerAutoplay();
    if (banner.planId) {
      setPlanId(banner.planId);
      document.getElementById(`pi-plan-${banner.planId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      listaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ── WhatsApp de ventas ──────────────────────────────────────────────────────
  // Un cliente se identifica con su nombre y cédula. Un administrador puede estar
  // enseñando la pantalla a otra persona, así que en su caso no se ponen sus datos.
  const pedirPorWhatsApp = () => {
    if (!plan) return;
    const partes = [
      `Hola, me interesa el plan de ${plan.nombre} a $${plan.precio} al mes.`,
      !esAdmin && clientData?.nombre
        ? `Soy ${clientData.nombre}${clientData.cedula ? `, cédula ${clientData.cedula}` : ''}.`
        : '',
      '¿Me pueden dar más información?',
    ];
    const texto = encodeURIComponent(partes.filter(Boolean).join(' '));
    window.open(`https://wa.me/${WHATSAPP_VENTAS}?text=${texto}`, '_blank');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />

      <div style={{ flex: 1, overflowY: 'auto' as const }}>
        {/* Cabecera de la pantalla, fija mientras se hace scroll */}
        <div
          style={{
            background: 'var(--sn-superficie)', padding: '12px 16px', display: 'flex', alignItems: 'center',
            gap: 12, borderBottom: '1px solid var(--sn-borde)', position: 'sticky', top: 0, zIndex: 10,
          }}
        >
          <button
            onClick={() => navigate(-1)}
            aria-label="Volver"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--sn-accion)' }}
          >
            <BackIcon />
          </button>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--sn-texto)', flex: 1 }}>Promociones Internet</div>
          <button type="button" className="pi-compartir" onClick={() => setMostrarCompartir(true)}>
            <CompartirIcon /> Compartir
          </button>
        </div>

        <div style={{ padding: 16, maxWidth: 500, margin: '0 auto' }}>
          {/* ── Carrusel de banners ── */}
          <div
            ref={carruselRef}
            className="pi-carrusel"
            onScroll={alDesplazarCarrusel}
            onTouchStart={detenerAutoplay}
            onPointerDown={detenerAutoplay}
            role="region"
            aria-roledescription="carrusel"
            aria-label="Promociones de internet"
          >
            {BANNERS.map(banner => {
              const planDelBanner = PLANES.find(p => p.id === banner.planId);
              return (
                <button
                  key={banner.clave}
                  type="button"
                  className={`pi-slide${banner.variante ? ` pi-slide--${banner.variante}` : ''}`}
                  onClick={() => tocarBanner(banner)}
                >
                  <FibraDecorativa />
                  <span className="pi-slide-kicker">{banner.kicker}</span>
                  <span className="pi-slide-cifra">
                    {banner.previo && <span className="pi-slide-previo">{banner.previo}</span>}
                    <span className="pi-slide-numero">{banner.numero}</span>
                    <span className="pi-slide-unidad">{banner.unidad}</span>
                  </span>
                  <span className="pi-slide-texto">{banner.texto}</span>
                  <span className="pi-slide-pie">
                    <span className="pi-slide-precio">
                      {planDelBanner ? <>${planDelBanner.precio} <small>/mes</small></> : 'IVA incluido'}
                    </span>
                    <span className="pi-slide-accion">{banner.accion} <FlechaIcon /></span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="pi-puntos">
            {BANNERS.map((banner, i) => (
              <button
                key={banner.clave}
                type="button"
                className={`pi-punto${i === bannerActivo ? ' pi-punto--activo' : ''}`}
                aria-label={`Ver promoción ${i + 1} de ${BANNERS.length}`}
                aria-current={i === bannerActivo}
                onClick={() => { detenerAutoplay(); irABanner(i); }}
              >
                <span />
              </button>
            ))}
          </div>

          {/* ── Planes ── */}
          <div ref={listaRef} className="pi-seccion-titulo" style={{ scrollMarginTop: 70 }}>
            Elige tu plan · IVA incluido
          </div>

          <div role="radiogroup" aria-label="Planes de internet" className="pi-planes">
            {PLANES.map(p => {
              const activo = p.id === planId;
              return (
                <button
                  key={p.id}
                  id={`pi-plan-${p.id}`}
                  type="button"
                  role="radio"
                  aria-checked={activo}
                  className={`pi-plan${activo ? ' pi-plan--activo' : ''}`}
                  onClick={() => setPlanId(p.id)}
                >
                  <span className="pi-plan-cabecera">
                    <span className="pi-plan-titulo">
                      <span className="pi-plan-radio" aria-hidden="true" />
                      <span className="pi-plan-nombre">{p.nombre}</span>
                      {p.etiqueta && <span className="pi-plan-etiqueta">{p.etiqueta}</span>}
                    </span>
                    <span className="pi-plan-precio">
                      <strong>${p.precio}</strong>
                      <span>al mes</span>
                    </span>
                  </span>

                  <span className="pi-plan-frase">{p.frase}</span>

                  <span className="pi-velocidad" aria-hidden="true">
                    <span style={{ width: `${Math.round((p.megas / MEGAS_MAXIMO) * 100)}%` }} />
                  </span>

                  <span className="pi-beneficios">
                    {p.beneficios.map(b => (
                      <span key={b} className="pi-beneficio"><CheckIcon />{b}</span>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Lo que traen todos ── */}
          <div className="pi-incluye">
            <div className="pi-seccion-titulo">Todos los planes incluyen</div>
            <ul className="pi-incluye-lista">
              {INCLUYE.map(item => (
                <li key={item}><CheckIcon />{item}</li>
              ))}
            </ul>
          </div>

          <p className="pi-nota">
            Precios mensuales con IVA incluido, los mismos de sisnetel.com. Por WhatsApp te confirmamos
            la cobertura en tu sector y agendamos la instalación.
          </p>
        </div>

        {/* Única acción principal de la pantalla: fija abajo mientras se hace scroll */}
        <div className="pi-cta">
          <button type="button" className="pi-cta-boton" onClick={pedirPorWhatsApp} disabled={!plan}>
            <WhatsAppIcon />
            {plan ? <>Pedir {plan.nombre} por WhatsApp <small>${plan.precio}/mes</small></> : 'Elige un plan'}
          </button>
        </div>
      </div>

      <BottomNav />

      <CompartirPlanesModal show={mostrarCompartir} onHide={() => setMostrarCompartir(false)} />
    </div>
  );
};

export default PromocionesInternet;
